import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  fetchProjectChecklists,
  createProjectChecklist,
  updateProjectChecklist,
  deleteProjectChecklist,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  type ProjectChecklistRow,
  type ProjectChecklistItemRow,
  type ProjectChecklistItemKind,
} from '../api/projects';
import Modal from './Modal';
import {
  fetchChecklistTemplates,
  createChecklistTemplate,
  type ProjectChecklistTemplate,
  type ProjectChecklistTemplateItem,
} from '../api/checklistTemplates';
import './SalesChecklistCards.css';
import './ProjectChecklistsTab.css';

function filterTemplateRowsBySubsectionInclude(
  rows: ProjectChecklistTemplateItem[],
  include: Record<number, boolean>
): ProjectChecklistTemplateItem[] {
  const out: ProjectChecklistTemplateItem[] = [];
  let activeSubsectionIdx: number | null = null;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    if (row.item_kind === 'section') {
      out.push(row);
      activeSubsectionIdx = null;
      continue;
    }
    if (row.item_kind === 'subsection') {
      activeSubsectionIdx = i;
      if (include[i] !== false) out.push(row);
      continue;
    }
    if (activeSubsectionIdx == null) {
      out.push(row);
      continue;
    }
    if (include[activeSubsectionIdx] !== false) out.push(row);
  }
  return out;
}

interface ProjectChecklistsTabProps {
  projectId: number;
  viewerUserId: number;
  canEdit: boolean;
}

function mergeChecklist(
  list: ProjectChecklistRow[],
  updated: ProjectChecklistRow
): ProjectChecklistRow[] {
  return list.map((c) => (c.id === updated.id ? updated : c));
}

const ProjectChecklistsTab: React.FC<ProjectChecklistsTabProps> = ({
  projectId,
  viewerUserId,
  canEdit,
}) => {
  const [checklists, setChecklists] = useState<ProjectChecklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [editingChecklistId, setEditingChecklistId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editItemLabels, setEditItemLabels] = useState<Record<number, string>>({});

  const [templates, setTemplates] = useState<ProjectChecklistTemplate[]>([]);
  const [addChecklistModalOpen, setAddChecklistModalOpen] = useState(false);
  /** Second step: pick template + preview + Cancel/Create */
  const [addChecklistTemplateStep, setAddChecklistTemplateStep] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  /** Template preview: row index → include (subsection rows when template has subsections; else checkbox rows). */
  const [templatePreviewInclude, setTemplatePreviewInclude] = useState<Record<number, boolean>>({});

  const checklistsRef = useRef(checklists);
  checklistsRef.current = checklists;

  const reload = useCallback(async () => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setChecklists([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchProjectChecklists(projectId, viewerUserId);
      setChecklists(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load checklists.');
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, viewerUserId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchChecklistTemplates(viewerUserId);
        if (!cancelled) setTemplates(list);
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerUserId]);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusyKey(key);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      try {
        const data = await fetchProjectChecklists(projectId, viewerUserId);
        setChecklists(data);
      } catch {
        /* keep prior list */
      }
    } finally {
      setBusyKey(null);
    }
  };

  const startEdit = (cl: ProjectChecklistRow) => {
    setEditingChecklistId(cl.id);
    setEditTitle(cl.title);
    setEditItemLabels(Object.fromEntries(cl.items.map((i) => [i.id, i.label])));
  };

  const cancelEdit = () => {
    setEditingChecklistId(null);
    setEditTitle('');
    setEditItemLabels({});
  };

  const finishEditing = async () => {
    const clId = editingChecklistId;
    if (clId == null) return;
    const cl = checklistsRef.current.find((c) => c.id === clId);
    if (!cl) {
      cancelEdit();
      return;
    }
    const titleTrim = editTitle.trim();
    if (!titleTrim) {
      setEditTitle(cl.title);
      cancelEdit();
      return;
    }
    await withBusy(`done-edit-${clId}`, async () => {
      const start = checklistsRef.current.find((c) => c.id === clId);
      if (!start) {
        cancelEdit();
        return;
      }
      let working: ProjectChecklistRow = start;
      if (titleTrim !== start.title) {
        working = await updateProjectChecklist(projectId, clId, {
          user_id: viewerUserId,
          title: titleTrim,
        });
      }
      for (const it of working.items) {
        const draft = (editItemLabels[it.id] ?? it.label).trim();
        if (draft !== it.label) {
          const updated = await updateChecklistItem(projectId, clId, it.id, {
            user_id: viewerUserId,
            label: draft,
          });
          working = {
            ...working,
            items: working.items.map((x) => (x.id === updated.id ? updated : x)),
          };
        }
      }
      setChecklists((prev) => mergeChecklist(prev, working));
      cancelEdit();
    });
  };

  const closeAddChecklistModal = () => {
    setAddChecklistModalOpen(false);
    setAddChecklistTemplateStep(false);
    setTemplatePreviewInclude({});
  };

  const openAddChecklistModal = () => {
    if (!canEdit) return;
    if (editingChecklistId != null) {
      cancelEdit();
    }
    setError('');
    void (async () => {
      try {
        const list = await fetchChecklistTemplates(viewerUserId);
        setTemplates(list);
        setAddChecklistTemplateStep(false);
        setSelectedTemplateId(list[0] ? String(list[0].id) : '');
        setAddChecklistModalOpen(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load templates.');
        setTemplates([]);
        setAddChecklistTemplateStep(false);
        setSelectedTemplateId('');
        setAddChecklistModalOpen(true);
      }
    })();
  };

  const createBlankChecklistNow = () => {
    if (!canEdit) return;
    void withBusy('add-checklist', async () => {
      const created = await createProjectChecklist(projectId, { user_id: viewerUserId });
      setChecklists((prev) => [...prev, created]);
      startEdit(created);
      closeAddChecklistModal();
    });
  };

  const enterTemplateStep = () => {
    setAddChecklistTemplateStep(true);
    setSelectedTemplateId((prev) =>
      templates.length === 0
        ? ''
        : templates.some((t) => String(t.id) === prev)
          ? prev
          : String(templates[0].id)
    );
  };

  const confirmAddChecklistFromTemplate = () => {
    if (!canEdit) return;
    void withBusy('add-checklist', async () => {
      const t = templates.find((x) => String(x.id) === selectedTemplateId);
      if (!t) {
        setError('Choose a template.');
        return;
      }
      const sourceRows: ProjectChecklistTemplateItem[] =
        t.templateItems.length > 0
          ? t.templateItems
          : t.itemLabels.map((label, i) => ({ label, item_kind: 'checkbox' as const, sort_order: i }));
      const hasSubsections = sourceRows.some((r) => r.item_kind === 'subsection');
      const filtered = hasSubsections
        ? filterTemplateRowsBySubsectionInclude(sourceRows, templatePreviewInclude)
        : sourceRows.filter((row, i) => {
            if (row.item_kind !== 'checkbox') return true;
            return templatePreviewInclude[i] !== false;
          });
      const created = await createProjectChecklist(projectId, {
        user_id: viewerUserId,
        title: t.title,
      });
      const itemRows: ProjectChecklistItemRow[] = [];
      for (const row of filtered) {
        const r = await createChecklistItem(projectId, created.id, {
          user_id: viewerUserId,
          label: row.label.trim() || 'Item',
          item_kind: row.item_kind as ProjectChecklistItemKind,
        });
        itemRows.push(r);
      }
      const full: ProjectChecklistRow = { ...created, items: itemRows };
      setChecklists((prev) => [...prev, full]);
      startEdit(full);
      closeAddChecklistModal();
    });
  };

  const openSaveTemplateModal = () => {
    const clId = editingChecklistId;
    if (clId == null) return;
    const cl = checklistsRef.current.find((c) => c.id === clId);
    setError('');
    setSaveTemplateName((editTitle.trim() || cl?.title || 'My template').trim());
    setSaveTemplateModalOpen(true);
  };

  const confirmSaveTemplate = () => {
    const clId = editingChecklistId;
    if (clId == null) return;
    const cl = checklistsRef.current.find((c) => c.id === clId);
    if (!cl) return;
    const name = saveTemplateName.trim();
    if (!name) {
      setError('Enter a name for this template.');
      return;
    }
    const titleDraft = editTitle.trim() || cl.title;
    const template_items = cl.items
      .map((it) => {
        const lab = (editItemLabels[it.id] ?? it.label).trim();
        const kind: ProjectChecklistItemKind =
          it.item_kind === 'section' || it.item_kind === 'subsection' ? it.item_kind : 'checkbox';
        return { label: lab, item_kind: kind };
      })
      .filter((row) => row.label.length > 0);
    void withBusy('save-template', async () => {
      await createChecklistTemplate({
        user_id: viewerUserId,
        name,
        title: titleDraft,
        template_items,
      });
      const list = await fetchChecklistTemplates(viewerUserId);
      setTemplates(list);
      setSaveTemplateModalOpen(false);
    });
  };

  const handleDeleteChecklist = (checklistId: number) => {
    if (!canEdit) return;
    if (!window.confirm('Delete this checklist and all of its items?')) return;
    void withBusy(`del-cl-${checklistId}`, async () => {
      await deleteProjectChecklist(projectId, checklistId, viewerUserId);
      setChecklists((prev) => prev.filter((c) => c.id !== checklistId));
      if (editingChecklistId === checklistId) cancelEdit();
    });
  };

  const handleToggleItem = (checklistId: number, item: ProjectChecklistItemRow, checked: boolean) => {
    if (!canEdit) return;
    if (item.item_kind && item.item_kind !== 'checkbox') return;
    if (item.is_checked === checked) return;
    void withBusy(`chk-${item.id}`, async () => {
      const updated = await updateChecklistItem(projectId, checklistId, item.id, {
        user_id: viewerUserId,
        is_checked: checked,
      });
      setChecklists((prev) =>
        prev.map((c) =>
          c.id !== checklistId
            ? c
            : { ...c, items: c.items.map((it) => (it.id === updated.id ? updated : it)) }
        )
      );
    });
  };

  const handleAddItem = (checklistId: number) => {
    if (!canEdit) return;
    void withBusy(`add-item-${checklistId}`, async () => {
      const row = await createChecklistItem(projectId, checklistId, { user_id: viewerUserId });
      setChecklists((prev) =>
        prev.map((c) => (c.id === checklistId ? { ...c, items: [...c.items, row] } : c))
      );
      setEditItemLabels((prev) => ({ ...prev, [row.id]: row.label }));
    });
  };

  const handleDeleteItem = (checklistId: number, itemId: number) => {
    if (!canEdit) return;
    void withBusy(`del-item-${itemId}`, async () => {
      await deleteChecklistItem(projectId, checklistId, itemId, viewerUserId);
      setChecklists((prev) =>
        prev.map((c) =>
          c.id !== checklistId ? c : { ...c, items: c.items.filter((it) => it.id !== itemId) }
        )
      );
      setEditItemLabels((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    });
  };

  const setItemLabelDraft = (itemId: number, value: string) => {
    setEditItemLabels((prev) => ({ ...prev, [itemId]: value }));
  };

  const selectedAddTemplate = useMemo(
    () => templates.find((t) => String(t.id) === selectedTemplateId),
    [templates, selectedTemplateId]
  );

  const templatePreviewUsesSubsectionToggles = useMemo(
    () => Boolean(selectedAddTemplate?.templateItems?.some((r) => r.item_kind === 'subsection')),
    [selectedAddTemplate]
  );

  useEffect(() => {
    const t = templates.find((x) => String(x.id) === selectedTemplateId);
    if (!t?.templateItems?.length) {
      setTemplatePreviewInclude({});
      return;
    }
    const hasSub = t.templateItems.some((r) => r.item_kind === 'subsection');
    const next: Record<number, boolean> = {};
    t.templateItems.forEach((row, i) => {
      if (hasSub) {
        if (row.item_kind === 'subsection') next[i] = true;
      } else if (row.item_kind === 'checkbox') {
        next[i] = true;
      }
    });
    setTemplatePreviewInclude(next);
  }, [selectedTemplateId, templates]);

  const templatePreviewBody = useMemo(() => {
    const t = selectedAddTemplate;
    if (!t?.templateItems.length) return null;
    const useSub = templatePreviewUsesSubsectionToggles;
    const nodes: React.ReactNode[] = [];
    let subsectionIncluded = true;
    for (let i = 0; i < t.templateItems.length; i += 1) {
      const row = t.templateItems[i]!;
      if (row.item_kind === 'section') {
        subsectionIncluded = true;
        nodes.push(
          <div key={`${t.id}-p-${i}`} className="project-checklist-preview-section">
            {row.label.trim() || '—'}
          </div>
        );
        continue;
      }
      if (row.item_kind === 'subsection') {
        if (useSub) {
          subsectionIncluded = templatePreviewInclude[i] !== false;
          nodes.push(
            <label key={`${t.id}-p-${i}`} className="project-checklist-preview-subsection-row">
              <input
                type="checkbox"
                checked={subsectionIncluded}
                onChange={(e) =>
                  setTemplatePreviewInclude((prev) => ({ ...prev, [i]: e.target.checked }))
                }
              />
              <span>{row.label.trim() || '—'}</span>
            </label>
          );
        } else {
          nodes.push(
            <div key={`${t.id}-p-${i}`} className="project-checklist-preview-subsection">
              {row.label.trim() || '—'}
            </div>
          );
        }
        continue;
      }
      if (useSub) {
        const dimmed = !subsectionIncluded;
        nodes.push(
          <div
            key={`${t.id}-p-${i}`}
            className={`project-checklist-preview-leaf-row${dimmed ? ' project-checklist-preview-leaf-row--excluded' : ''}`}
          >
            <span className="project-checklist-preview-leaf-glyph" aria-hidden="true" />
            <span>{row.label.trim() || '—'}</span>
          </div>
        );
      } else {
        nodes.push(
          <label key={`${t.id}-p-${i}`} className="project-checklist-preview-checkbox-row">
            <input
              type="checkbox"
              checked={templatePreviewInclude[i] !== false}
              onChange={(e) =>
                setTemplatePreviewInclude((prev) => ({ ...prev, [i]: e.target.checked }))
              }
            />
            <span>{row.label.trim() || '—'}</span>
          </label>
        );
      }
    }
    return nodes;
  }, [selectedAddTemplate, templatePreviewInclude, templatePreviewUsesSubsectionToggles]);

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-detail-panel-placeholder">Sign in to view checklists.</p>;
  }

  return (
    <section
      className="project-checklists-tab sales-checklist-root sales-checklist-root--primary"
      aria-label="Project checklists"
    >
      <h3 className="sales-checklist-heading">Checklists</h3>
      <p className="sales-checklist-subheading">
        Use checkboxes to track progress. Checklists can include section and subsection headings. Edit a checklist to
        change its title and lines, save it as a template for everyone, or remove it.
      </p>

      <div className="project-checklists-toolbar">
        {canEdit && (
          <button
            type="button"
            className="project-detail-secondary-btn"
            onClick={openAddChecklistModal}
            disabled={busyKey === 'add-checklist' || loading}
          >
            Add checklist
          </button>
        )}
      </div>

      <Modal
        isOpen={addChecklistModalOpen}
        onClose={closeAddChecklistModal}
        title="Add checklist"
        narrow={!addChecklistTemplateStep}
        wide={addChecklistTemplateStep}
      >
        <>
          <div className="modal-body">
            <div className="project-checklist-template-modal">
              {!addChecklistTemplateStep ? (
                <>
                  <p className="project-checklist-template-hint project-checklist-template-hint--tight">
                    Blank creates a checklist right away. From template lets you preview lines before creating.
                  </p>
                  <div className="project-checklist-start-buttons" role="group" aria-label="Checklist starting point">
                    <button
                      type="button"
                      className="project-checklist-start-btn"
                      onClick={() => void createBlankChecklistNow()}
                      disabled={busyKey === 'add-checklist'}
                    >
                      {busyKey === 'add-checklist' ? 'Creating…' : 'Blank checklist'}
                    </button>
                    <button
                      type="button"
                      className="project-checklist-start-btn"
                      onClick={() => enterTemplateStep()}
                      disabled={busyKey === 'add-checklist'}
                    >
                      From template
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="project-checklist-template-back"
                    onClick={() => setAddChecklistTemplateStep(false)}
                    disabled={busyKey === 'add-checklist'}
                  >
                    ← Choose different start
                  </button>
                  {templates.length > 0 ? (
                    <>
                      <label className="project-checklist-template-select-label">
                        Template
                        <select
                          className="project-checklist-template-select"
                          value={selectedTemplateId}
                          onChange={(e) => setSelectedTemplateId(e.target.value)}
                          disabled={busyKey === 'add-checklist'}
                        >
                          {templates.map((tpl) => (
                            <option key={tpl.id} value={String(tpl.id)}>
                              {tpl.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedAddTemplate && (
                        <div className="project-checklist-template-preview" aria-label="Template preview">
                          <div className="project-checklist-template-preview-label">Preview</div>
                          <div className="project-checklist-template-preview-title">{selectedAddTemplate.title}</div>
                          {selectedAddTemplate.templateItems.length > 0 ? (
                            <div className="project-checklist-template-preview-body">
                              <p className="project-checklist-template-preview-hint">
                                {templatePreviewUsesSubsectionToggles
                                  ? 'Uncheck a subsection to skip that entire group when creating the checklist. Major section headings are always included.'
                                  : 'Uncheck any line to skip it when creating the checklist.'}
                              </p>
                              {templatePreviewBody}
                            </div>
                          ) : selectedAddTemplate.itemLabels.length > 0 ? (
                            <ul className="project-checklist-template-preview-list">
                              {selectedAddTemplate.itemLabels.map((label, i) => (
                                <li key={`${selectedAddTemplate.id}-p-${i}`}>{label.trim() ? label : '—'}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="project-checklist-template-preview-empty">No lines in this template (title only).</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="project-checklist-template-hint project-checklist-template-hint--after">
                      No saved templates yet. Edit a checklist and use &quot;Save as template&quot;, or go back and choose
                      blank checklist.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          {addChecklistTemplateStep && (
            <div className="modal-actions">
              <button
                type="button"
                className="project-detail-secondary-btn"
                onClick={closeAddChecklistModal}
                disabled={busyKey === 'add-checklist'}
              >
                Cancel
              </button>
              <button
                type="button"
                className="project-detail-primary-btn"
                onClick={() => void confirmAddChecklistFromTemplate()}
                disabled={busyKey === 'add-checklist' || templates.length === 0 || !selectedAddTemplate}
              >
                {busyKey === 'add-checklist' ? 'Creating…' : 'Create'}
              </button>
            </div>
          )}
        </>
      </Modal>

      <Modal
        isOpen={saveTemplateModalOpen}
        onClose={() => setSaveTemplateModalOpen(false)}
        title="Save as template"
        narrow
      >
        <>
          <div className="modal-body">
            <div className="project-checklist-template-modal">
              <p className="project-checklist-template-hint project-checklist-template-hint--tight">
                Saves this checklist&apos;s title, section/subsection headings, and checkbox lines. New checklists from
                this template start with all checkbox items unchecked.
              </p>
              <label className="project-checklist-template-select-label">
                Template name
                <input
                  type="text"
                  className="sales-checklist-input project-checklist-template-name-input"
                  value={saveTemplateName}
                  onChange={(e) => setSaveTemplateName(e.target.value)}
                  placeholder="e.g. Site survey"
                  autoFocus
                  disabled={busyKey === 'save-template'}
                />
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="project-detail-secondary-btn"
              onClick={() => setSaveTemplateModalOpen(false)}
              disabled={busyKey === 'save-template'}
            >
              Cancel
            </button>
            <button
              type="button"
              className="project-detail-primary-btn"
              onClick={() => void confirmSaveTemplate()}
              disabled={busyKey === 'save-template'}
            >
              {busyKey === 'save-template' ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </>
      </Modal>

      {loading && <p className="project-detail-panel-placeholder">Loading checklists…</p>}
      {error && (
        <p className="sales-checklist-attach-error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && checklists.length === 0 && (
        <p className="project-detail-panel-placeholder">
          {canEdit
            ? 'No checklists yet. Use “Add checklist” to create one.'
            : 'No checklists on this project yet.'}
        </p>
      )}

      {!loading && !error && checklists.length > 0 && (
        <div className="sales-checklist-cards">
          {checklists.map((cl) => {
            const isEditing = canEdit && editingChecklistId === cl.id;
            const checkableItems = cl.items.filter((i) => !i.item_kind || i.item_kind === 'checkbox');
            const allComplete =
              checkableItems.length > 0 && checkableItems.every((i) => i.is_checked);
            const doneBusy = busyKey === `done-edit-${cl.id}`;

            return (
              <article
                key={cl.id}
                className={`sales-checklist-card${allComplete ? ' sales-checklist-card--complete' : ''}`}
              >
                {allComplete && (
                  <div className="sales-checklist-card-top">
                    <span className="sales-checklist-completed-badge">Completed</span>
                  </div>
                )}

                <div className="project-checklist-card-head">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        className="sales-checklist-input project-checklist-title-field"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="Checklist title"
                        disabled={doneBusy}
                        aria-label="Checklist title"
                      />
                      <div className="project-checklist-card-head-actions">
                        <button
                          type="button"
                          className="project-detail-secondary-btn project-checklist-head-btn"
                          onClick={() => void finishEditing()}
                          disabled={doneBusy}
                        >
                          {doneBusy ? 'Saving…' : 'Done'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="sales-checklist-card-title project-checklist-card-title-static">{cl.title}</h4>
                      {canEdit && (
                        <div className="project-checklist-card-head-actions">
                          <button
                            type="button"
                            className="project-detail-secondary-btn project-checklist-head-btn"
                            onClick={() => startEdit(cl)}
                            disabled={
                              (editingChecklistId != null && editingChecklistId !== cl.id) ||
                              busyKey === `done-edit-${cl.id}`
                            }
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <ul className="sales-checklist-list">
                  {cl.items.map((item) => {
                    const kind = item.item_kind ?? 'checkbox';
                    if (kind === 'section') {
                      return (
                        <li key={item.id} className="sales-checklist-item project-checklist-item--section">
                          {isEditing ? (
                            <div className="project-checklist-edit-row project-checklist-edit-row--heading">
                              <span className="project-checklist-heading-badge" aria-hidden>
                                Section
                              </span>
                              <input
                                type="text"
                                className="sales-checklist-input project-checklist-edit-line-input project-checklist-edit-line-input--section"
                                value={editItemLabels[item.id] ?? item.label}
                                onChange={(e) => setItemLabelDraft(item.id, e.target.value)}
                                placeholder="Section title"
                                disabled={doneBusy}
                                aria-label="Section title"
                              />
                              <button
                                type="button"
                                className="project-checklist-icon-btn project-checklist-icon-btn--danger"
                                title="Remove"
                                aria-label="Remove section"
                                disabled={busyKey === `del-item-${item.id}` || doneBusy}
                                onClick={() => handleDeleteItem(cl.id, item.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          ) : (
                            <h5 className="project-checklist-section-title">{item.label?.trim() ? item.label : '—'}</h5>
                          )}
                        </li>
                      );
                    }
                    if (kind === 'subsection') {
                      return (
                        <li key={item.id} className="sales-checklist-item project-checklist-item--subsection">
                          {isEditing ? (
                            <div className="project-checklist-edit-row project-checklist-edit-row--heading">
                              <span className="project-checklist-heading-badge project-checklist-heading-badge--sub" aria-hidden>
                                Subsection
                              </span>
                              <input
                                type="text"
                                className="sales-checklist-input project-checklist-edit-line-input"
                                value={editItemLabels[item.id] ?? item.label}
                                onChange={(e) => setItemLabelDraft(item.id, e.target.value)}
                                placeholder="Subsection title"
                                disabled={doneBusy}
                                aria-label="Subsection title"
                              />
                              <button
                                type="button"
                                className="project-checklist-icon-btn project-checklist-icon-btn--danger"
                                title="Remove"
                                aria-label="Remove subsection"
                                disabled={busyKey === `del-item-${item.id}` || doneBusy}
                                onClick={() => handleDeleteItem(cl.id, item.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          ) : (
                            <h6 className="project-checklist-subsection-title">{item.label?.trim() ? item.label : '—'}</h6>
                          )}
                        </li>
                      );
                    }
                    return (
                      <li key={item.id} className="sales-checklist-item sales-checklist-item--check">
                        {isEditing ? (
                          <div className="project-checklist-edit-row">
                            <label
                              className="sales-checklist-check-label project-checklist-edit-check-wrap"
                              htmlFor={`proj-check-${cl.id}-${item.id}`}
                            >
                              <input
                                id={`proj-check-${cl.id}-${item.id}`}
                                type="checkbox"
                                className="sales-checklist-checkbox"
                                checked={item.is_checked}
                                onChange={(e) => handleToggleItem(cl.id, item, e.target.checked)}
                                disabled={busyKey === `chk-${item.id}`}
                              />
                            </label>
                            <input
                              type="text"
                              className="sales-checklist-input project-checklist-edit-line-input"
                              value={editItemLabels[item.id] ?? item.label}
                              onChange={(e) => setItemLabelDraft(item.id, e.target.value)}
                              placeholder="Item description"
                              disabled={doneBusy}
                              aria-label="Item text"
                            />
                            <button
                              type="button"
                              className="project-checklist-icon-btn project-checklist-icon-btn--danger"
                              title="Remove item"
                              aria-label="Remove item"
                              disabled={busyKey === `del-item-${item.id}` || doneBusy}
                              onClick={() => handleDeleteItem(cl.id, item.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        ) : (
                          <label className="sales-checklist-check-label" htmlFor={`proj-check-${cl.id}-${item.id}`}>
                            <input
                              id={`proj-check-${cl.id}-${item.id}`}
                              type="checkbox"
                              className="sales-checklist-checkbox"
                              checked={item.is_checked}
                              onChange={(e) => handleToggleItem(cl.id, item, e.target.checked)}
                              disabled={!canEdit || busyKey === `chk-${item.id}`}
                              aria-label={`Done: ${item.label || 'item'}`}
                            />
                            <span
                              className={
                                item.is_checked ? 'project-checklist-view-label--done' : undefined
                              }
                            >
                              {item.label?.trim() ? item.label : '—'}
                            </span>
                          </label>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {isEditing && (
                  <div className="project-checklist-edit-footer">
                    <button
                      type="button"
                      className="project-detail-secondary-btn"
                      onClick={() => handleAddItem(cl.id)}
                      disabled={busyKey === `add-item-${cl.id}` || doneBusy}
                    >
                      Add item
                    </button>
                    <button
                      type="button"
                      className="project-detail-secondary-btn project-checklist-save-template-btn"
                      onClick={() => openSaveTemplateModal()}
                      disabled={doneBusy}
                      title="Save title and lines as a reusable template"
                    >
                      <FontAwesomeIcon icon={faFloppyDisk} aria-hidden />
                      <span>Save as template</span>
                    </button>
                    <button
                      type="button"
                      className="project-detail-secondary-btn project-checklist-delete-list-btn"
                      onClick={() => handleDeleteChecklist(cl.id)}
                      disabled={busyKey === `del-cl-${cl.id}` || doneBusy}
                    >
                      Delete checklist
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ProjectChecklistsTab;
